import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const stanceItems = [
  { label: '対話', desc: '答えを持ち込まず、組織の内側にある意味を一緒に見つけます。' },
  { label: '継続', desc: 'プロジェクトが終わっても、組織に残る力を育てることを意識します。' },
  { label: '誠実', desc: '「できないこと」は正直に伝えます。できることに、全力を尽くします。' },
  { label: '長期', desc: '3年後、5年後に振り返ったとき、「あのとき正しかった」と思える仕事を。' },
];

export default function StanceSection() {
  const left = useScrollAnimation();
  const right = useScrollAnimation();
  const items = useStaggerAnimation(stanceItems.length);

  return (
    <section className="texture-stance py-28 md:py-40 bg-warm-white relative overflow-hidden">

      {/* ── 「長期・対話・深さ・螺旋」をテーマにした装飾 ── */}

      {/* 右上の「螺旋状の同心円」— 時間の積み重なり */}
      {/* 中心からずれた同心円群（螺旋を暗示） */}
      <div className="absolute top-4 right-4 lg:top-8 lg:right-12 pointer-events-none">
        <div className="absolute rounded-full border-2 border-accent-amber/32" style={{ width: 40, height: 40, top: 0, right: 0 }} />
        <div className="absolute rounded-full border border-accent-gold/28" style={{ width: 72, height: 72, top: -16, right: -16 }} />
        <div className="absolute rounded-full border border-accent-amber/20" style={{ width: 110, height: 110, top: -35, right: -35 }} />
        <div className="absolute rounded-full border border-accent-gold/14" style={{ width: 155, height: 155, top: -57, right: -57 }} />
        <div className="absolute rounded-full border border-accent-amber/9 hidden lg:block" style={{ width: 210, height: 210, top: -85, right: -85 }} />
        {/* 中心点 */}
        <div className="absolute w-2 h-2 rounded-full bg-accent-amber/45" style={{ top: 19, right: 19 }} />
        <div className="absolute w-1 h-1 rounded-full bg-accent-gold/55" style={{ top: 20, right: 20 }} />
      </div>

      {/* 右上ドットグリッド */}
      <div className="absolute top-0 right-0 w-28 h-28 lg:w-52 lg:h-52 pointer-events-none overflow-hidden">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(201,168,76,0.25) 1.5px, transparent 1.5px)', backgroundSize: '18px 18px' }} />
      </div>

      {/* 左下の「時間軸」— 長期を示す縦の積み重なり */}
      <div className="absolute -bottom-8 -left-8 pointer-events-none">
        {/* 大きな同心円 */}
        <div className="absolute rounded-full border border-accent-amber/16" style={{ width: 160, height: 160, top: -80, left: -80 }} />
        <div className="absolute rounded-full border border-accent-gold/10 hidden sm:block" style={{ width: 280, height: 280, top: -140, left: -140 }} />
        <div className="absolute rounded-full border border-accent-amber/7 hidden md:block" style={{ width: 400, height: 400, top: -200, left: -200 }} />
      </div>

      {/* 中央左の「対話の往復」— 双方向の矢印 */}
      <div className="absolute left-16 top-1/2 -translate-y-1/2 pointer-events-none hidden xl:block">
        <div className="flex flex-col gap-3">
          {/* 右向き矢印（発信） */}
          <div className="flex items-center gap-1">
            <div className="w-8 h-px bg-accent-teal/25" />
            <div className="w-0 h-0" style={{ borderTop: '3px solid transparent', borderBottom: '3px solid transparent', borderLeft: '5px solid rgba(74,158,142,0.25)' }} />
          </div>
          {/* 左向き矢印（受信・対話） */}
          <div className="flex items-center gap-1">
            <div className="w-0 h-0" style={{ borderTop: '3px solid transparent', borderBottom: '3px solid transparent', borderRight: '5px solid rgba(212,122,46,0.25)' }} />
            <div className="w-8 h-px bg-accent-amber/25" />
          </div>
          {/* 右向き矢印（深化） */}
          <div className="flex items-center gap-1">
            <div className="w-8 h-px bg-accent-gold/20" />
            <div className="w-0 h-0" style={{ borderTop: '3px solid transparent', borderBottom: '3px solid transparent', borderLeft: '5px solid rgba(201,168,76,0.20)' }} />
          </div>
        </div>
      </div>

      {/* 中央下の「深さ」を示す菱形 */}
      <div className="absolute bottom-8 left-1/4 pointer-events-none">
        <div className="w-6 h-6 lg:w-12 lg:h-12 border-2 border-accent-teal/25 rotate-45" />
        <div className="absolute w-3 h-3 lg:w-6 lg:h-6 border border-accent-teal/30 rotate-45" style={{ left: 6, top: 6 }} />
        <div className="absolute w-1.5 h-1.5 rounded-full bg-accent-teal/35" style={{ left: 9, top: 9 }} />
      </div>
      <div className="absolute bottom-12 left-[28%] w-3 h-3 lg:w-5 lg:h-5 rounded-full bg-accent-amber/28 pointer-events-none" />

      {/* 右中央の「時間の積み重なり」— 水平線群 */}
      <div className="absolute right-12 top-1/2 -translate-y-1/2 pointer-events-none hidden lg:block">
        {[0, 1, 2, 3, 4, 5, 6].map(i => (
          <div
            key={i}
            className="mb-3"
            style={{
              width: 24 + i * 6,
              height: 1,
              background: `rgba(${i % 3 === 0 ? '212,122,46' : i % 3 === 1 ? '201,168,76' : '74,158,142'},${0.28 - i * 0.02})`,
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-20 items-start">
          {/* Left */}
          <div
            ref={left.ref as React.RefObject<HTMLDivElement>}
            className="md:col-span-6 transition-all duration-700"
            style={{ opacity: left.isVisible ? 1 : 0, transform: left.isVisible ? 'translateX(0)' : 'translateX(-32px)' }}
          >
            <div className="flex items-center gap-4 mb-10">
              <span className="block w-6 h-px bg-accent-teal" />
              <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Stance</span>
            </div>
            <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight mb-12 text-3xl md:text-4xl">
              短期成果だけを求める方には、<br />向かないかもしれません。
            </h2>
            <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp mb-10">
              私たちの仕事は、速く、派手に、目立つことを目的としていません。
              組織の意味を丁寧に掘り起こし、それが社会に届き、信頼として蓄積されるまでを
              一緒に歩むことを大切にしています。
            </p>
            <div className="border-l-2 border-accent-amber pl-8 py-2">
              <p className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-relaxed text-lg">
                しかし、3年後も誇れる言葉を残したい方とは、<br />深く伴走できます。
              </p>
            </div>
          </div>

          {/* Right */}
          <div
            ref={right.ref as React.RefObject<HTMLDivElement>}
            className="md:col-span-6 transition-all duration-700"
            style={{ opacity: right.isVisible ? 1 : 0, transform: right.isVisible ? 'translateX(0)' : 'translateX(32px)' }}
          >
            <p className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase mb-8">私たちが大切にすること</p>
            <div
              ref={items.ref as React.RefObject<HTMLDivElement>}
              className="space-y-0 border-t border-divider"
            >
              {stanceItems.map((item, i) => (
                <div
                  key={item.label}
                  className="grid grid-cols-12 gap-6 py-7 border-b border-divider items-start transition-all duration-500"
                  style={{
                    opacity: items.visibleCount > i ? 1 : 0,
                    transform: items.visibleCount > i ? 'translateX(0)' : 'translateX(20px)',
                    transitionDelay: `${i * 100}ms`,
                  }}
                >
                  <div className="col-span-3">
                    <span className="font-serif-jp text-base font-light text-charcoal tracking-normal-jp">{item.label}</span>
                  </div>
                  <div className="col-span-9">
                    <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-12">
              <blockquote className="font-garamond text-lg md:text-xl italic text-muted-brown leading-relaxed">
                &ldquo;The meaning is already there.<br />We just help you see it.&rdquo;
              </blockquote>
              <div className="mt-8 pt-8 border-t border-divider">
                <Link
                  to="/message"
                  className="inline-flex items-center gap-2 text-sm font-sans-jp text-accent-teal tracking-wide-jp hover:opacity-70 transition-opacity duration-300 whitespace-nowrap"
                >
                  <span>代表メッセージを読む</span>
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
